"use client";

import React from "react";
import { TriangleAlert as AlertTriangle, ArrowRight, Flame, PhoneCall, ShieldAlert, Waves, Droplets, Zap, Pipette } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
interface SymptomItem {
  id: string;
  title: string;
  badge: string;
  severity: "destructive" | "default" | "secondary";
  description: string;
  actionText: string;
  link: string;
  icon: React.ElementType;
}
const emergencySymptoms: SymptomItem[] = [{
  id: "burst-pipe",
  title: "Burst or Gushing Pipe",
  badge: "Immediate Action Required",
  severity: "destructive",
  description: "Water actively spraying or gushing inside walls, ceilings, or crawlspaces. Shut off main valve immediately.",
  actionText: "Emergency Pipe Repair",
  link: "/pipe-repair/",
  icon: Waves
}, {
  id: "sewage-backup",
  title: "Raw Sewage Backup",
  badge: "Health Hazard",
  severity: "destructive",
  description: "Foul-smelling black water coming up through basement drains, bathtubs, or toilets. Indicates mainline failure.",
  actionText: "Sewer Line Diagnostics",
  link: "/sewer-line-repair/",
  icon: AlertTriangle
}, {
  id: "overflowing-toilet",
  title: "Toilet Won't Stop Overflowing",
  badge: "Active Risk",
  severity: "destructive",
  description: "Water rising over bowl edges continuously. Immediate clean-up and blockage removal prevents structural floor rot.",
  actionText: "Fix Toilet Overflow",
  link: "/toilet-repair/",
  icon: ShieldAlert
}, {
  id: "no-hot-water",
  title: "Water Heater Leaking or Cold",
  badge: "Urgent Repair",
  severity: "default",
  description: "Standing pooling water around the tank base, popping noises, or sudden lack of hot water across all fixtures.",
  actionText: "Water Heater Service",
  link: "/water-heater-repair/",
  icon: Flame
}, {
  id: "hidden-slab-leak",
  title: "Unexplained Wet Spots or Warm Floors",
  badge: "Structural Alert",
  severity: "secondary",
  description: "Damp carpet, sudden spikes in water bills, or hot spots on foundation slabs signaling hidden pressurized line leaks.",
  actionText: "Electronic Leak Detection",
  link: "/leak-detection-repair/",
  icon: Droplets
}, {
  id: "drain-stoppage",
  title: "Multiple Blocked Drains",
  badge: "Severe Clog",
  severity: "secondary",
  description: "Simultaneous slow drainage or gurgling noises across sinks and tubs, indicating severe main stack obstruction.",
  actionText: "Emergency Drain Clearing",
  link: "/drain-cleaning/",
  icon: Pipette
}];
export default function Symptoms() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };
  return <section id="emergencysymptomsgrid" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold uppercase tracking-wider mb-3">
              <Zap className="size-3 text-accent" />
              Jonesboro Triage Guide
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-default">
              Recognize the Symptoms of a Plumbing Emergency
            </h2>
            <p className="mt-3 text-base sm:text-lg text-muted-foreground font-default">
              Plumbing problems escalate fast. If you notice any of the warning signs below, our Jonesboro emergency dispatch team is ready 24/7.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button asChild size="lg" className="shadow-sm font-semibold">
              <a href="tel:4708479076">
                <PhoneCall className="mr-2 size-4 text-accent" />
                Call Dispatch Now
              </a>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/emergency-plumbing/">
                All Emergency Services
              </Link>
            </Button>
          </div>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {emergencySymptoms.map((item, i) => {
          const IconComponent = item.icon;
          return <motion.div key={item.id} data-index={i} variants={cardVariants} className="h-full flex flex-col">
                <Card className="h-full flex flex-col justify-between border border-border shadow-xs hover:shadow-md transition-shadow bg-card text-card-foreground">
                  <CardHeader className="space-y-3 pb-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="p-2.5 rounded-lg bg-secondary text-primary inline-flex">
                        <IconComponent className="size-5" />
                      </div>
                      <Badge variant={item.severity} className="text-[11px] font-semibold tracking-normal">
                        {item.badge}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-bold font-default text-foreground pt-1">
                      {item.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="pt-0">
                    <CardDescription className="text-muted-foreground text-sm leading-relaxed font-default">
                      {item.description}
                    </CardDescription>
                  </CardContent>

                  <CardFooter className="pt-4 border-t border-border/60 mt-auto flex items-center justify-between">
                    <Button asChild variant="ghost" size="sm" className="px-0 text-primary font-semibold hover:bg-transparent hover:text-primary/80 group inline-flex items-center gap-1.5">
                      <Link to={item.link}>
                        <span>{item.actionText}</span>
                        <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        <div className="mt-12 rounded-xl bg-secondary/50 border border-border p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-start sm:items-center gap-4">
            <div className="p-3 rounded-full bg-accent/20 text-accent-foreground shrink-0">
              <AlertTriangle className="size-6 text-accent" />
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-bold font-default text-foreground">
                Don't see your specific plumbing symptom listed?
              </h3>
              <p className="text-sm text-muted-foreground font-default mt-0.5">
                Our licensed Jonesboro plumbers diagnose and resolve any pipe, drain, or fixture emergency immediately.
              </p>
            </div>
          </div>
          <Button asChild variant="default" className="shrink-0 w-full sm:w-auto">
            <Link to="/contact/">
              Contact 24/7 Plumber
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}